import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ShiftTasksPayload } from 'api/project';
import { DateValue } from 'components/atoms/due-date-picker';
import { Modal as AntModal } from 'components/atoms/modal';
import { DateType, getNewDates } from 'utils/date-shift';
import { PhasesTable } from './phases-table';
import { RadioOptions } from './radio-options';
import { Conflict } from './types';
import { getUpdatedActions, sortDataSource } from './utils';

interface Props {
  visible: boolean;
  onClose: () => void;
  rearrangeTasks: (updatedActions?: ShiftTasksPayload) => void;
  actions: ShiftTasksPayload;
  setActions: React.Dispatch<React.SetStateAction<ShiftTasksPayload>>;
  loading: boolean;
  dataSource: Conflict[];
  setDataSource: React.Dispatch<React.SetStateAction<Conflict[]>>;
}

export enum TaskConflictRadioOptions {
  AutoAdjustTask = 1,
  AutoAdjustPhase = 2,
}

const Modal = styled(AntModal)`
  .ant-table.ant-table-small .ant-table-tbody > tr > td {
    padding: 0 !important;
  }
  .ant-table.ant-table-small .ant-table-thead > tr > th {
    padding: 0 var(--spacing-05);
  }
`;

const TextWrapper = styled.div`
  width: 70%;
  word-wrap: break-word;
`;

export const DateConflictModal: FC<Props> = ({
  visible,
  onClose,
  rearrangeTasks,
  actions,
  setActions,
  loading,
  dataSource,
  setDataSource,
}) => {
  const [radioOption, setRadioOption] = useState<TaskConflictRadioOptions>(
    TaskConflictRadioOptions.AutoAdjustTask,
  );

  const [startDateExtendedArray, setStartDateExtendedArray] = useState<
    number[]
  >([]);
  const [dueDateExtendedArray, setDueDateExtendedArray] = useState<number[]>(
    [],
  );

  const numberOfTasks = useMemo(
    () => dataSource.reduce((sum, current) => sum + current.children.length, 0),
    [dataSource],
  );

  useEffect(() => {
    if (radioOption === TaskConflictRadioOptions.AutoAdjustTask) {
      setStartDateExtendedArray([]);
      setDueDateExtendedArray([]);

      const arr = dataSource.map((phase) => {
        const [phaseStartDate, phaseDueDate] = phase.currentDates;
        let newPhaseDueDate = phaseDueDate;

        const newChildren = phase?.children?.map((task) => {
          const [newTaskStartDate, newTaskDueDate] = task.parentTaskDates[0]
            ? getNewDates(
                task.parentTaskDates[0],
                task.duration,
                DateType.StartDate,
              )
            : task.parentTaskDates[1]
            ? getNewDates(
                task.parentTaskDates[1],
                task.duration,
                DateType.StartDate,
              )
            : getNewDates(phaseStartDate, task.duration, DateType.StartDate);

          newPhaseDueDate = moment(newTaskDueDate).isAfter(
            moment(newPhaseDueDate),
          )
            ? (newTaskDueDate as string)
            : newPhaseDueDate;

          return {
            ...task,
            newDates: [newTaskStartDate, newTaskDueDate],
          };
        });

        setStartDateExtendedArray((prev) =>
          prev?.filter((id) => id !== phase.key),
        );

        setDueDateExtendedArray((prev) => {
          if (newPhaseDueDate !== phaseDueDate) {
            const id = phase.key;
            const oldArr = prev?.filter((key) => key !== id);

            return [...oldArr, id];
          }

          return prev;
        });

        return {
          ...phase,
          children: newChildren,
          newDates: [phaseStartDate, newPhaseDueDate],
        };
      });

      setDataSource(arr as Conflict[]);
    } else {
      setStartDateExtendedArray([]);
      setDueDateExtendedArray([]);
      const arr = dataSource.map((phase) => {
        const [phaseStartDate, phaseDueDate] = phase.currentDates;
        let newPhaseDueDate = phaseDueDate;
        let newStartDate = phaseStartDate;

        const newChildren = phase?.children?.map((task) => {
          const [taskStartDate, taskDueDate] = task.currentDates;

          newStartDate = newStartDate
            ? moment(taskStartDate).isBefore(newStartDate)
              ? taskStartDate
              : newStartDate
            : taskStartDate;

          newPhaseDueDate = moment(taskDueDate).isAfter(moment(newPhaseDueDate))
            ? taskDueDate
            : newPhaseDueDate;

          return {
            ...task,
            newDates: task.currentDates,
          };
        });

        setStartDateExtendedArray((prev) => {
          if (newStartDate !== phaseStartDate) {
            const id = phase.key;

            const oldArr = prev?.filter((key) => key !== id);

            return [...oldArr, id];
          }

          return prev;
        });

        setDueDateExtendedArray((prev) => {
          if (newPhaseDueDate !== phaseDueDate) {
            const id = phase.key;
            const oldArr = prev?.filter((key) => key !== id);

            return [...oldArr, id];
          }

          return prev;
        });

        return {
          ...phase,
          newDates: [newStartDate, newPhaseDueDate],
          children: newChildren,
        };
      });

      setDataSource(arr as Conflict[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioOption]);

  const handleDateChange = useCallback(
    (newDates: [DateValue, DateValue], record: any) => {
      if (record.type === 'TASK') {
        const phases = dataSource.map((phase) => {
          if (phase.key === record.projectPhase.projectPhaseId) {
            let newPhaseStartDate = phase.currentDates[0];
            let newPhaseDueDate = phase.currentDates[1];
            const newChildren = phase?.children?.map((task) => {
              if (task.key === record.key) {
                const [newTaskStartDate, newTaskDueDate] = newDates;

                newPhaseStartDate = moment(newTaskStartDate).isBefore(
                  moment(newPhaseStartDate),
                )
                  ? (newTaskStartDate as string)
                  : newPhaseStartDate;

                newPhaseDueDate = moment(newTaskDueDate).isAfter(
                  moment(newPhaseDueDate),
                )
                  ? (newTaskDueDate as string)
                  : newPhaseDueDate;

                return {
                  ...task,
                  newDates,
                };
              }

              return task;
            });

            newChildren.forEach((task) => {
              const [, taskDueDate] = task.newDates;

              newPhaseDueDate = moment(taskDueDate).isAfter(
                moment(newPhaseDueDate),
              )
                ? (taskDueDate as string)
                : newPhaseDueDate;
            });

            newPhaseStartDate = moment(phase.currentDates[0]).isBefore(
              moment(newPhaseStartDate),
            )
              ? phase.currentDates[0]
              : newPhaseStartDate;

            newPhaseDueDate = moment(phase.currentDates[1]).isAfter(
              moment(newPhaseDueDate),
            )
              ? phase.currentDates[1]
              : newPhaseDueDate;

            setStartDateExtendedArray((prev) => {
              const id = phase.key;

              if (moment(newPhaseStartDate).isBefore(phase.currentDates[0])) {
                const oldArr = prev?.filter((key) => key !== id);

                return [...oldArr, id];
              }

              return [...prev.filter((key) => key !== id)];
            });

            setDueDateExtendedArray((prev) => {
              const id = phase.key;

              if (moment(newPhaseDueDate).isAfter(phase.currentDates[1])) {
                const oldArr = prev?.filter((key) => key !== id);

                return [...oldArr, id];
              }

              return [...prev.filter((key) => key !== id)];
            });

            return {
              ...phase,
              children: newChildren,
              newDates: [newPhaseStartDate, newPhaseDueDate],
            };
          }

          return phase;
        });

        setDataSource(phases as Conflict[]);
      }
    },
    [dataSource, setDataSource],
  );

  const handleRearrange = useCallback(() => {
    const updatedActions = getUpdatedActions(actions, dataSource);

    setActions(() => updatedActions);
    rearrangeTasks(updatedActions);
  }, [actions, dataSource, rearrangeTasks, setActions]);

  return (
    <Modal
      visible={visible}
      title={<FormattedMessage id="tasks.date.conflicts" />}
      onCancel={onClose}
      onOk={handleRearrange}
      okText={<FormattedMessage id="templates.rearrange_tasks.title" />}
      destroyOnClose
      maskClosable={false}
      okButtonProps={{
        danger: true,
      }}
      width="696px"
      confirmLoading={loading}
    >
      <TextWrapper>
        <FormattedMessage
          id="tasks.date_conflict_description"
          values={{
            strong: (chunks) => <strong>{chunks}</strong>,
            taskNumber: numberOfTasks,
          }}
        />
      </TextWrapper>

      <RadioOptions
        selected={radioOption}
        onChange={(latest) => setRadioOption(latest)}
      />

      <PhasesTable
        dataSource={sortDataSource(dataSource)}
        selected={radioOption}
        handleDateChange={handleDateChange}
        startDateExtendedArray={startDateExtendedArray}
        dueDateExtendedArray={dueDateExtendedArray}
      />
    </Modal>
  );
};
